/**
 * PersonPicker — the ONE searchable person chooser.
 *
 * Every surface that picks a single person (call reassignment, text
 * assignment, task assignee, booking assignment, follow-up task owner, the
 * call-search "assigned to" filter, team settings) renders this instead of a
 * `<select>` full of every name on the roster. Managers had dozens of options
 * to cycle through; now they type two letters.
 *
 * It is a WAI-ARIA combobox with an attached listbox:
 *   - the text input carries role="combobox" + aria-expanded/-controls/
 *     -activedescendant, the popup carries role="listbox", rows role="option"
 *   - ArrowUp/ArrowDown move the active row, Enter picks it, Escape closes
 *     WITHOUT picking, Tab just leaves (focus is never trapped — the popup is
 *     driven entirely by aria-activedescendant, so DOM focus stays on the input)
 *   - the list opens on focus showing everyone (up to `limit`), so a three-person
 *     roster needs no typing at all; typing filters by name OR email, case-
 *     insensitively, and a "+N more" footer says when to keep typing
 *   - the extra `options` rows filter with the SAME fold, across label and
 *     hint, so a caller whose roster is keyed by something other than an email
 *     (the call-group screen picks by 3CX extension) is just as searchable
 *   - mouse/touch: rows commit on mousedown (blur would otherwise close the
 *     list first), the same trick `PeopleChipPicker` has always used
 *
 * The popup is portalled to <body> and positioned fixed against the input:
 * several of these live inside `overflow-x-auto` tables and scroll panels,
 * where an absolutely-positioned dropdown would be clipped the way a native
 * `<select>` popup never is.
 *
 * Form integration is deliberately boring: with `name`, it renders a hidden
 * input holding exactly the string the old `<select>` submitted, so every
 * action, intent, permission check and journaled reason on the server is
 * untouched. The server stays authoritative — this is convenience only.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

export type Person = { email: string; name: string | null }

/**
 * A non-person row in the same list — the department pool, "me (reminder)",
 * "Anyone", "Me". Its `value` is submitted verbatim, exactly as the matching
 * `<option value=…>` used to be.
 */
export type PersonPickerOption = {
	value: string
	label: string
	/** muted second line (e.g. why the row is unavailable) */
	hint?: string
	disabled?: boolean
}

/** How many rows the popup shows at once before the "+N more" footer. */
const DEFAULT_LIMIT = 8

/** What a person reads as when there is no name on the roster row. */
export function personLabel(p: Person): string {
	return p.name ?? p.email
}

/**
 * Case-folded for searching: accents dropped and the punctuation people forget
 * removed, so "jose" finds "José", "obrien" finds "O'Brien" (straight or curly
 * apostrophe), and "danak" finds "dana.k@…". Typing is now the only way to
 * reach these surfaces, so a name a CSR cannot spell exactly must still be
 * findable.
 */
export function fold(s: string): string {
	return s
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/['\u2018\u2019`._-]/g, '')
		.toLowerCase()
}

/**
 * Label OR hint, folded exactly like a person row.
 *
 * Extra rows are not always "Me" and "— department pool —": a roster keyed by
 * something other than an email arrives here as `options` (the call-group
 * screen picks by 3CX extension, which is what its action reads), and those
 * rows must be as findable as `people` — "obrien" has to reach O'Brien, and the
 * extension printed in the hint has to be typeable. Matching the hint is why
 * this is not simply `personMatches` with a fake email.
 */
export function optionMatches(o: PersonPickerOption, query: string): boolean {
	if (!query) return true
	const q = fold(query)
	if (!q) return true
	return fold(o.label).includes(q) || fold(o.hint ?? '').includes(q)
}

/** Name OR email, case- and accent-insensitive substring. */
export function personMatches(p: Person, query: string): boolean {
	if (!query) return true
	const q = fold(query)
	if (!q) return true
	return fold(p.name ?? '').includes(q) || fold(p.email).includes(q)
}

/**
 * The shared roster filter (PeopleChipPicker uses it too): trims and folds the
 * query, keeps name-or-email matches, and reports the full match count so
 * callers can say "+N more".
 */
export function filterPeople(
	people: Array<Person>,
	query: string,
	limit = DEFAULT_LIMIT,
): { matches: Array<Person>; total: number } {
	const q = query.trim()
	const all = q ? people.filter((p) => personMatches(p, q)) : people
	return { matches: all.slice(0, limit), total: all.length }
}

/** One rendered listbox row — an extra option, or someone off the roster. */
type Row = {
	kind: 'option' | 'person'
	value: string
	label: string
	hint?: string
	disabled?: boolean
}

type Anchor = {
	left: number
	width: number
	top?: number
	bottom?: number
	maxHeight: number
}

/** Anchor the popup to the control, flipping above when there is no room below. */
function measure(el: HTMLElement): Anchor {
	const r = el.getBoundingClientRect()
	const viewport =
		typeof window === 'undefined' ? 768 : window.innerHeight || 768
	const below = viewport - r.bottom - 8
	const above = r.top - 8
	const flip = below < 160 && above > below
	const maxHeight = Math.max(96, Math.min(256, flip ? above : below))
	const left = Math.max(4, r.left)
	const width = Math.max(r.width, 224)
	return flip
		? { left, width, bottom: viewport - r.top + 4, maxHeight }
		: { left, width, top: r.bottom + 4, maxHeight }
}

export function PersonPicker({
	people,
	name,
	label,
	value: controlledValue,
	defaultValue = '',
	onChange,
	options = [],
	placeholder = 'Type a name or email…',
	disabled = false,
	required = false,
	clearable = true,
	limit = DEFAULT_LIMIT,
	size = 'md',
	className,
	autoFocus = false,
	id: idProp,
	noMatchLabel = 'No one matches that',
	emptyLabel = 'No assignable people',
}: {
	/** the roster (already loaded — no server round-trip per keystroke) */
	people: Array<Person>
	/** form field name; omit for a picker driven purely by `onChange` */
	name?: string
	/** accessible name for the control — required, it labels the combobox */
	label: string
	/** controlled value (an email, an option value, or '') */
	value?: string
	/** uncontrolled starting value */
	defaultValue?: string
	onChange?: (value: string, person: Person | null) => void
	/** extra non-person rows, listed above the roster */
	options?: Array<PersonPickerOption>
	placeholder?: string
	disabled?: boolean
	required?: boolean
	/** show the × that clears the selection (off where empty is not a value) */
	clearable?: boolean
	limit?: number
	size?: 'sm' | 'md'
	className?: string
	autoFocus?: boolean
	id?: string
	noMatchLabel?: string
	emptyLabel?: string
}) {
	const reactId = useId()
	const inputId = idProp ?? `pp-${reactId}`
	const listId = `${inputId}-list`

	const [uncontrolled, setUncontrolled] = useState(defaultValue)
	const isControlled = controlledValue !== undefined
	const value = isControlled ? controlledValue : uncontrolled

	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	/** false until the user types — so focusing shows the WHOLE list, not just
	 *  the row matching the current selection's label */
	const [dirty, setDirty] = useState(false)
	const [active, setActive] = useState(0)
	const [anchor, setAnchor] = useState<Anchor | null>(null)

	const wrapRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLUListElement>(null)

	const q = dirty ? query.trim().toLowerCase() : ''

	const { rows, total } = useMemo(() => {
		const optionRows: Array<Row> = options
			.filter((o) => optionMatches(o, q))
			.map((o) => ({
				kind: 'option' as const,
				value: o.value,
				label: o.label,
				...(o.hint === undefined ? {} : { hint: o.hint }),
				...(o.disabled === undefined ? {} : { disabled: o.disabled }),
			}))
		const { matches, total: peopleTotal } = filterPeople(people, q, limit)
		const personRows: Array<Row> = matches.map((p) => ({
			kind: 'person' as const,
			value: p.email,
			label: personLabel(p),
			...(p.name ? { hint: p.email } : {}),
		}))
		return {
			rows: [...optionRows, ...personRows],
			total: optionRows.length + peopleTotal,
		}
	}, [options, people, q, limit])

	/**
	 * What the closed input reads: the picked row's label, or the raw value.
	 *
	 * The old native <select> collapsed to its full option text ("Name (email)").
	 * A bare name is ambiguous where two people share one — and on surfaces
	 * whose value persists and is glanced at later (the call-search "Assigned to"
	 * filter round-trips through defaultValue across page loads, and the team
	 * settings pickers) nobody could tell WHICH one is set without reopening the
	 * list. So the email is appended, but only when the display name collides.
	 */
	const selectedLabel = useMemo(() => {
		const opt = options.find((o) => o.value === value)
		if (opt) return opt.label
		if (!value) return ''
		const person = people.find((p) => p.email === value)
		if (!person) return value
		const text = personLabel(person)
		const collides =
			people.some((p) => p.email !== person.email && personLabel(p) === text) ||
			options.some((o) => o.label === text)
		return collides ? `${text} (${person.email})` : text
	}, [options, people, value])

	const selectedPerson = useMemo(
		() => people.find((p) => p.email === value) ?? null,
		[people, value],
	)

	/** First selectable row — a disabled row must never be the active one. */
	const firstEnabled = useMemo(() => {
		const i = rows.findIndex((r) => !r.disabled)
		return i === -1 ? 0 : i
	}, [rows])
	/** `active` is a hint; the row list changes under it as the query narrows. */
	const hinted = rows[active]
	const activeIndex = hinted && !hinted.disabled ? active : firstEnabled

	const reposition = useCallback(() => {
		if (wrapRef.current) setAnchor(measure(wrapRef.current))
	}, [])

	// autoFocus fires during the commit that mounts the input — BEFORE the
	// wrapper's ref is attached — so openList() finds no element to measure.
	// Catch that case here, once refs exist.
	useEffect(() => {
		if (open && anchor === null) reposition()
	}, [open, anchor, reposition])

	// keep the popup glued to the control while the page scrolls behind it
	useEffect(() => {
		if (!open) return
		const onScroll = () => reposition()
		window.addEventListener('scroll', onScroll, true)
		window.addEventListener('resize', onScroll)
		return () => {
			window.removeEventListener('scroll', onScroll, true)
			window.removeEventListener('resize', onScroll)
		}
	}, [open, reposition])

	// keep the aria-active row visible in a scrolled popup
	useEffect(() => {
		if (!open) return
		const el = listRef.current?.querySelector<HTMLElement>(
			'[data-active="true"]',
		)
		// jsdom has no scrollIntoView
		if (el && typeof el.scrollIntoView === 'function') {
			el.scrollIntoView({ block: 'nearest' })
		}
	}, [open, activeIndex, rows.length])

	function openList() {
		if (disabled) return
		reposition()
		setOpen(true)
	}

	/** Escape / blur: put the input back to the selection, pick nothing. */
	function closeList() {
		setOpen(false)
		setDirty(false)
		setQuery('')
		setActive(0)
	}

	function commit(row: Row) {
		if (row.disabled) return
		if (!isControlled) setUncontrolled(row.value)
		onChange?.(
			row.value,
			row.kind === 'person'
				? (people.find((p) => p.email === row.value) ?? null)
				: null,
		)
		closeList()
		inputRef.current?.focus()
	}

	function clear() {
		if (!isControlled) setUncontrolled('')
		onChange?.('', null)
		closeList()
		inputRef.current?.focus()
	}

	/** Next selectable row in `delta` direction; stays put if there is none. */
	function step(delta: number): number {
		for (let i = activeIndex + delta; i >= 0 && i < rows.length; i += delta) {
			if (!rows[i]?.disabled) return i
		}
		return activeIndex
	}

	function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			if (!open) {
				openList()
				return
			}
			setActive(step(1))
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			if (!open) {
				openList()
				return
			}
			setActive(step(-1))
		} else if (e.key === 'Enter') {
			// Enter only belongs to the picker while the list is open; closed, it
			// keeps submitting the surrounding form the way the <select> did
			if (!open) return
			e.preventDefault()
			const row = rows[activeIndex]
			if (row && !row.disabled) commit(row)
		} else if (e.key === 'Escape') {
			if (!open) return
			// don't let a surrounding modal/panel also treat this Escape as its own
			e.stopPropagation()
			closeList()
		} else if (e.key === 'Tab') {
			// never trap focus — just tidy up on the way out
			closeList()
		}
	}

	const rosterEmpty = people.length === 0 && options.length === 0
	const shell =
		size === 'sm' ? 'h-7 text-xs gap-1 px-1.5' : 'h-9 text-sm gap-1.5 px-2'

	const activeRow = open ? rows[activeIndex] : undefined
	// name the email once — selectedLabel already carries it when names collide
	const announcement =
		selectedPerson && !selectedLabel.includes(selectedPerson.email)
			? `${selectedLabel} (${selectedPerson.email})`
			: selectedLabel

	return (
		<div ref={wrapRef} className={cn('relative', className)}>
			{name ? <input type="hidden" name={name} value={value} /> : null}
			<div
				className={cn(
					'border-input bg-background flex w-full items-center rounded-md border',
					shell,
					disabled && 'opacity-60',
					open && 'ring-ring ring-2',
				)}
			>
				<Icon
					name="magnifying-glass"
					size="xs"
					className="text-muted-foreground shrink-0"
				/>
				<input
					ref={inputRef}
					id={inputId}
					role="combobox"
					aria-expanded={open}
					aria-haspopup="listbox"
					aria-autocomplete="list"
					{...(open ? { 'aria-controls': listId } : {})}
					{...(activeRow
						? { 'aria-activedescendant': `${listId}-r${activeIndex}` }
						: {})}
					aria-label={label}
					aria-required={required || undefined}
					aria-disabled={disabled || undefined}
					autoComplete="off"
					spellCheck={false}
					disabled={disabled}
					autoFocus={autoFocus}
					placeholder={rosterEmpty ? emptyLabel : placeholder}
					value={dirty ? query : selectedLabel}
					onChange={(e) => {
						setQuery(e.target.value)
						setDirty(true)
						setActive(0)
						if (!open) openList()
					}}
					onFocus={() => {
						openList()
						// the whole label is selected, so the first keystroke replaces it
						inputRef.current?.select()
					}}
					onBlur={closeList}
					onKeyDown={onKeyDown}
					className="min-w-0 flex-1 border-0 bg-transparent p-0 outline-none"
				/>
				{clearable && value && !disabled ? (
					<button
						type="button"
						// mousedown would blur the input and close the list first
						onMouseDown={(e) => {
							e.preventDefault()
							clear()
						}}
						aria-label={`Clear ${label}`}
						className="hover:bg-muted shrink-0 rounded-full p-0.5"
					>
						<Icon name="cross-1" size="xs" />
					</button>
				) : null}
			</div>
			{open && anchor !== null && typeof document !== 'undefined'
				? createPortal(
						<ul
							ref={listRef}
							id={listId}
							role="listbox"
							aria-label={label}
							style={{
								position: 'fixed',
								left: anchor.left,
								width: anchor.width,
								maxHeight: anchor.maxHeight,
								...(anchor.top === undefined
									? { bottom: anchor.bottom }
									: { top: anchor.top }),
							}}
							className="bg-background z-50 overflow-y-auto overscroll-contain rounded-md border py-1 shadow-lg"
						>
							{rows.map((row, i) => (
								<li
									key={`${row.kind}:${row.value}`}
									id={`${listId}-r${i}`}
									role="option"
									aria-selected={row.value === value}
									aria-disabled={row.disabled || undefined}
									data-active={i === activeIndex ? 'true' : 'false'}
									onMouseDown={(e) => {
										e.preventDefault()
										commit(row)
									}}
									onMouseEnter={() => {
										if (!row.disabled) setActive(i)
									}}
									className={cn(
										'px-2 py-1.5 text-sm',
										row.disabled
											? 'text-muted-foreground cursor-not-allowed'
											: 'cursor-pointer',
										i === activeIndex && !row.disabled && 'bg-muted',
									)}
								>
									<span className="font-medium">{row.label}</span>
									{row.hint ? (
										<span className="text-muted-foreground ml-1 text-xs">
											{row.hint}
										</span>
									) : null}
								</li>
							))}
							{rows.length === 0 ? (
								<li
									role="presentation"
									className="text-muted-foreground px-2 py-1.5 text-xs"
								>
									{rosterEmpty ? emptyLabel : noMatchLabel}
								</li>
							) : null}
							{total > rows.length ? (
								<li
									role="presentation"
									className="text-muted-foreground border-t px-2 py-1 text-[11px]"
								>
									+{total - rows.length} more — keep typing to narrow
								</li>
							) : null}
						</ul>,
						document.body,
					)
				: null}
			{/* what a screen reader hears after a pick, without stealing focus */}
			<span className="sr-only" aria-live="polite">
				{value ? `${label}: ${announcement}` : ''}
			</span>
		</div>
	)
}
